import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Bell, BellOff, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const NotificationManager = () => {
  const { api, user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkSubscriptionStatus();
    }
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const response = await api.get('/push/status');
      setIsSubscribed(response.data.subscribed);
    } catch (error) {
      console.error('Failed to check subscription status:', error);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }

    setIsLoading(true);

    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        toast.error('Please allow notifications to receive daily reminders');
        setIsLoading(false);
        return;
      }

      // Get VAPID public key from server
      const vapidResponse = await api.get('/push/vapid-public-key');
      const vapidPublicKey = vapidResponse.data.publicKey;

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      await api.post('/push/subscribe', {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });

      setIsSubscribed(true);
      toast.success('Notifications enabled! You\'ll receive daily reminders.');
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to enable notifications. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [api, isSupported]);

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);

    try {
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
      }

      // Update server
      await api.post('/push/unsubscribe');

      setIsSubscribed(false);
      toast.success('Notifications disabled');
    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast.error('Failed to disable notifications');
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const sendTestNotification = async () => {
    try {
      await api.post('/push/send-test');
      toast.success('Test notification sent!');
    } catch (error) {
      if (error.response?.status === 410) {
        setIsSubscribed(false);
        toast.error('Subscription expired. Please re-enable notifications.');
      } else {
        toast.error('Failed to send test notification');
      }
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <div className="card-bubbly bg-gradient-to-r from-blue-50 to-indigo-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Bell className="w-5 h-5 text-green-600" />
            </div>
          ) : (
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <BellOff className="w-5 h-5 text-slate-400" />
            </div>
          )}
          <div>
            <h4 className="font-bold text-slate-900">Daily Reminders</h4>
            <p className="text-sm text-slate-500">
              {isSubscribed 
                ? "You'll get reminders to train your brain!" 
                : "Never miss your daily brain workout"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isSubscribed && (
            <button
              onClick={sendTestNotification}
              className="px-3 py-2 bg-blue-100 text-blue-700 rounded-xl text-sm font-medium hover:bg-blue-200 transition-colors"
              disabled={isLoading}
            >
              Test
            </button>
          )}
          
          <button
            onClick={isSubscribed ? unsubscribe : subscribe}
            disabled={isLoading || permission === 'denied'}
            className={`
              px-4 py-2 rounded-xl font-bold text-sm transition-all
              ${isSubscribed 
                ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                : 'bg-primary text-black hover:bg-primary/90'
              }
              ${isLoading ? 'opacity-50 cursor-wait' : ''}
              ${permission === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {isLoading 
              ? '...' 
              : isSubscribed 
                ? 'Disable' 
                : 'Enable'}
          </button>
        </div>
      </div>

      {permission === 'denied' && (
        <div className="mt-3 p-3 bg-amber-50 rounded-xl text-sm text-amber-700 flex items-center gap-2">
          <X className="w-4 h-4" />
          Notifications are blocked. Please enable them in your browser settings.
        </div>
      )}
    </div>
  );
};

export default NotificationManager;
